// Shared source of truth for Learn hub articles.
// - /learn (src/pages/Learn.jsx) renders the full list from ARTICLES.
// - The homepage Resources callout (LearnCallout in src/App.jsx) renders
//   getFeaturedArticles() — so adding or retitling a guide here updates
//   both surfaces automatically.
//
// Ordering convention: append NEW guides to the END of ARTICLES (newest last).
// Featuring: set `featured: true` on up to FEATURED_COUNT articles to pin them
// on the homepage. Optional `featuredTitle` / `featuredSummary` provide a
// shorter homepage variant; the full title/summary are used when absent.
// If fewer than FEATURED_COUNT articles are flagged, the newest unflagged
// guides fill the remaining homepage slots automatically.
//
// SEO metadata: src/lib/pageMeta.js derives each guide's <title>/<meta
// description> from this list — `metaTitle` / `metaDescription` when present
// (SEO-tuned variants), else the plain `title` / `summary`. Structured data
// (src/lib/learnStructuredData.js) derives FAQ question text from `title`
// too, so retitling a guide here updates crawler-visible metadata everywhere.

export const FEATURED_COUNT = 3;

export const ARTICLES = [
  {
    to: "/learn/how-to-verify-a-legitimate-online-glp1-provider",
    metaDescription:
      "Knowing how to evaluate a telehealth provider before you share health information or pay protects you from programs that cut corners on safety and billing.",
    title: "How do you verify that an online GLP-1 provider is legitimate?",
    summary: "Four things worth checking before you commit to any program: LegitScript certification, transparent billing, independent licensed providers, and written cancellation terms.",
    tag: "Legitimacy",
    featured: true,
    featuredSummary: "Four things worth checking before you commit to any program — LegitScript certification, transparent billing, and more.",
  },
  {
    to: "/learn/is-compounded-semaglutide-safe-and-legal",
    metaDescription:
      "A direct answer, not marketing language: compounded medications are not FDA-approved, availability depends on your state, and providers decide individually.",
    title: "Is compounded semaglutide safe and legal?",
    summary: "A direct answer, not marketing language — what \"not FDA-approved\" means, how availability works, and the questions you have a right to ask any program.",
    tag: "Legitimacy",
  },
  {
    to: "/learn/what-is-legitscript-certification",
    metaDescription:
      "LegitScript independently reviews telehealth operators for legal compliance. Certification is publicly verifiable and required for healthcare ads on Google and Meta.",
    title: "What is LegitScript certification and why does it matter for telehealth?",
    summary: "Why an independent, publicly verifiable compliance review matters — and why Google and Meta require it before a telehealth operator can run healthcare ads.",
    tag: "Verification",
  },
  {
    to: "/learn/compounded-vs-branded-glp1",
    metaDescription:
      "Branded GLP-1s completed FDA new drug approval; compounded medications are not FDA-approved and differ in formulation, oversight, cost, and availability.",
    title: "What is the difference between compounded and branded GLP-1 medication?",
    summary: "Regulatory pathway, formulation, cost, and availability — what actually differs in practice, and why neither option is right for every patient.",
    tag: "Education",
    featured: true,
    featuredSummary: "Regulatory pathway, formulation, cost, and availability — what actually differs in practice.",
  },
  {
    to: "/learn/what-to-look-for-in-an-online-weight-loss-program",
    metaDescription:
      "A practical guide: independent licensed providers, written billing terms, pre-charge notice, third-party certification — and the warning signs to avoid.",
    title: "What should you look for — and avoid — in an online weight-loss program?",
    summary: "A practical guide to evaluating any program before you enroll: the five things to look for and the three warning signs to walk away from.",
    tag: "Safety",
  },
  {
    to: "/learn/how-does-an-online-weight-loss-evaluation-work",
    metaDescription:
      "The four steps of an online weight-management evaluation: structured health intake, licensed provider review, a real clinical decision, and ongoing supervision.",
    title: "How does an online medical weight-loss evaluation work?",
    summary: "The four steps of a real online evaluation — structured intake, licensed provider review, a genuine clinical decision, and ongoing supervision.",
    tag: "Education",
  },
  {
    to: "/learn/glp1-and-your-privacy-health-data",
    metaDescription:
      "Telehealth programs handling protected health information are subject to HIPAA. What to ask before you enroll, and how your data should — and shouldn't — be used.",
    title: "What happens to your health data when you enroll in an online GLP-1 program?",
    summary: "HIPAA coverage, what to ask before you enroll, how your data should be used — and the difference between pre-enrollment data and protected health information.",
    tag: "Safety",
  },
  {
    to: "/learn/what-does-a-medical-weight-loss-program-cost",
    metaDescription:
      "How to read the real price of any telehealth weight-management program: what's included, cash-pay vs insurance, and what honest cancellation terms require.",
    title: 'What does a medical weight-loss program actually cost — and what should "no hidden fees" mean?',
    summary: "How to read the real price of any program: what the stated price includes, the cash-pay picture, and what honest cancellation language requires.",
    tag: "Pricing",
    featured: true,
    featuredTitle: "What does a medical weight-loss program actually cost?",
    featuredSummary: "How to read the real price of any program — and what honest \"no hidden fees\" language requires.",
  },
  {
    to: "/learn/glp-1-plateau-what-to-do",
    metaTitle: "Hit a GLP-1 Plateau? What It Means (and What to Do) | Vyvron",
    metaDescription:
      "A GLP-1 plateau usually isn't treatment failure — it's a predictable metabolic equilibrium. Here's the science and what to discuss with your provider.",
    title: "Hit a GLP-1 plateau? What it means and what to do",
    summary: "A plateau on GLP-1 treatment isn't failure — it's predictable metabolism. The science behind what's happening and the right questions to bring to your provider.",
    tag: "Plateaus",
  },
  {
    to: "/learn/does-glp-1-cause-muscle-loss",
    metaTitle: "Do GLP-1 Medications Cause Muscle Loss? The 2026 Evidence | Vyvron",
    metaDescription:
      "GLP-1 weight loss includes some lean-mass loss — but recent research suggests it's not disproportionate when protein and resistance training are maintained. The evidence, plainly.",
    title: "Do GLP-1 medications cause muscle loss?",
    summary: "Some lean-mass loss happens alongside fat loss — but 2026 research shows it isn't disproportionate. The evidence, and the two interventions that protect muscle.",
    tag: "Muscle",
  },
  {
    to: "/learn/glp-1-side-effects-what-to-expect",
    metaTitle: "GLP-1 Side Effects: What to Expect (and What to Tell Your Provider) | Vyvron",
    metaDescription:
      "The common, usually mild-to-moderate GLP-1 side effects, the serious warnings to know, and when to contact your provider. Educational, not medical advice.",
    title: "GLP-1 side effects: what to expect",
    summary: "The common side effects (mostly GI), the FDA boxed warning you need to know, and exactly when to call your provider versus call 911.",
    tag: "Side Effects",
  },
  {
    to: "/learn/compounded-glp1-big-brands-2026",
    metaTitle: "Compounded GLP-1 in 2026: What Changed | Vyvron",
    title: "Compounded GLP-1 and the Big Telehealth Brands: What Changed in 2026",
    summary: "Several large telehealth players scaled back compounded GLP-1 in 2026. Here's what that means for access — and where eligible patients can still get care.",
    tag: "Education",
  },
  {
    to: "/learn/compounded-semaglutide-cost-guide",
    metaTitle: "Compounded Semaglutide Cost 2026: Real All-In Price | Vyvron",
    title: "What Compounded Semaglutide Actually Costs (And Why the Cheapest Number Isn't the Real One)",
    summary: "This guide breaks down what actually drives that spread, what to check before you trust a number, and what you'd pay through Vyvron.",
    tag: "Pricing",
  },
  {
    to: "/learn/compounded-glp1-legit-safety-checklist",
    metaTitle: "Is Compounded Semaglutide Safe? The Honest Answer | Vyvron",
    title: "Is Compounded Semaglutide Safe? The Straight Answer, Not the Spin",
    metaDescription: "Compounded semaglutide is not inherently unsafe, but it is not risk-free, and the risk is concentrated almost entirely in how and where it's sourced — not in the concept of compounding itself.",
    summary: "Search this question and you'll find two kinds of answers: marketing copy that waves the risk away, and manufacturer/regulator content with an obvious incentive to make compounding sound categorically dangerous. Neither is the full picture. Here's the honest one.",
    tag: "Safety",
  },
  {
    to: "/learn/compounded-glp1-503a-explainer",
    metaTitle: "Compounded GLP-1 & the 2026 FDA Proposal: Facts | Vyvron",
    title: "Compounded GLP-1 After the 2026 FDA Proposal: What's Actually Changing, and What Isn't",
    summary: "What the 2026 FDA proposal means for 503B outsourcing facilities, 503A patient-specific compounding, and access through licensed providers.",
    tag: "Education",
  },
  {
    to: "/learn/compounded-glp1-telehealth-changes",
    metaTitle: "Compounded GLP-1 Telehealth Changes: Your Options | Vyvron",
    title: "Big telehealth brands scaled back compounded GLP-1. Here's what your options are now.",
    metaDescription: "Some large telehealth platforms narrowed or exited compounded GLP-1 access. If your program changed, here's a clear, honest rundown of what happened and what to look for in a new provider.",
    summary: "Some large telehealth platforms narrowed or exited compounded GLP-1 access. If your program changed, here's a clear, honest rundown of what happened and what to look for in a new provider.",
    tag: "Education",
  },
];

export const TAG_COLORS = {
  Legitimacy: { bg: "#EFF6FF", text: "#1D4ED8" },
  Safety: { bg: "#ECFDF5", text: "#065F46" },
  Verification: { bg: "#F5F3FF", text: "#5B21B6" },
  Education: { bg: "#FFF7ED", text: "#92400E" },
  Pricing: { bg: "#FFF1F2", text: "#9F1239" },
  Plateaus: { bg: "#F0FDF4", text: "#166534" },
  Muscle: { bg: "#FEF3C7", text: "#78350F" },
  "Side Effects": { bg: "#FDF4FF", text: "#6B21A8" },
};

// Homepage featured guides: flagged articles first (list order), then the
// newest remaining guides (end of ARTICLES) to fill up to FEATURED_COUNT.
export function getFeaturedArticles(count = FEATURED_COUNT) {
  const flagged = ARTICLES.filter((a) => a.featured).slice(0, count);
  if (flagged.length < count) {
    const fill = ARTICLES.filter((a) => !a.featured)
      .slice(-(count - flagged.length))
      .reverse();
    flagged.push(...fill);
  }
  return flagged.map((a) => ({
    to: a.to,
    tag: a.tag,
    title: a.featuredTitle || a.title,
    summary: a.featuredSummary || a.summary,
  }));
}
